import React from 'react';
import PropTypes from 'prop-types';

import {Grid, Chip, Tooltip, Box, Typography, Popover} from 'views/components';
import {InsertEmoticon} from 'views/icons';
import {translate} from '../Translate';

import Emoji from './emoji/Emoji';
import EmojiPicker from './emoji/EmojiPicker';

import userUtil from 'utils/miscellaneous/user/user_util';

/**
 * Custom tooltip title
 * @param {object} props
 * @param {string} props.emoji the emoji reacted
 * @param {string} props.users comma separated string of user names
 * @return {ReactJSXElement}
 */
const EmojiDetails = (props) => {
  return (
    <Grid container spacing={0.5} justifyContent='center'>
      <Grid item>
        <Box
          fontSize='48px'
          minHeight='64px'
          minWidth='48px'
          color='black'
          display='flex'
          alignItems='center'
          sx={{backgroundColor: 'white', borderRadius: '4px'}}
        >
          <Emoji emoji={props.emoji}/>
        </Box>
      </Grid>
      <Grid item xs={12} sx={{display: 'flex', justifyContent: 'center'}}>
        <Typography sx={{textAlign: 'center', fontWeight: 'bold'}}>
          {props.users}
          <Typography component='span' display='inline' sx={{color: '#c4c4c7', fontWeight: 'bold'}}>
            {
              props.overflowLength > 0 ?
                ` ${translate('correspondances.Emoji.Reacted')} ${translate('correspondances.Emoji.PlusNOthers', {n: props.overflowLength})}` :
                ` ${translate('correspondances.Emoji.Reacted')}`
            }
          </Typography>
        </Typography>
      </Grid>
    </Grid>
  );
};
EmojiDetails.propTypes = {
  emoji: PropTypes.string.isRequired,
  users: PropTypes.string.isRequired,
  overflowLength: PropTypes.number.isRequired,
};

const EmojiBar = (props) => {
  const [openEmojiPicker, setOpenEmojiPicker] = React.useState(false);

  const {
    emojis,
    emojisWithRequesterData,
    onEmojiSelected,
    requesterRn,
  } = props;

  const emojiPickerAnchorRef = React.useRef();
  const emojiKeys = Object.keys(emojis);

  const onEmojiSelect = (selectedEmoji) => {
    onEmojiSelected(selectedEmoji.native);
    setOpenEmojiPicker(false);
  };

  return (
    <Grid container spacing={0.5} data-testid='emoji-bar'>
      {
        emojiKeys.map((emoji, i) => {
          const emojiWithData = emojisWithRequesterData[emoji] || {};
          const userNames = Object.values(emojiWithData).map((user) => {
            const userData = user.data;
            if (userData.userRn === requesterRn) {
              return;
            }
            return userUtil.getFullName(userData.firstName, userData.lastName);
          }).filter((e) => e);
          const userRns = emojis[emoji];
          const isSelected = userRns.find((rn) => rn === requesterRn);
          if (isSelected) {
            userNames.push(translate('correspondances.Emoji.You'));
          }
          const usersReacted = userNames.join(', '); ;
          const overflowLength = userRns.length - userNames.length;

          return (
            <Grid item key={i}>
              <Tooltip
                placement='top'
                title={<EmojiDetails emoji={emoji} users={usersReacted} overflowLength={overflowLength}/>}
                PopperProps={{
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, -8],
                      },
                    },
                  ],
                }}
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: 'black',
                      border: '1px solid grey',
                    },
                  },
                }}
                arrow
              >
                <Chip
                  icon={<div style={{fontSize: '16px'}}><Emoji emoji={emoji}/></div>}
                  label={emojis[emoji].length}
                  onClick={() => onEmojiSelected(emoji)}
                  color={isSelected ? 'primary' : undefined}
                  clickable
                />
              </Tooltip>
            </Grid>
          );
        })
      }
      <Grid item>
        <Chip
          ref={emojiPickerAnchorRef}
          icon={<div style={{fontSize: '16px', display: 'flex', alignItems: 'center'}}><InsertEmoticon/></div>}
          label={<Typography>+</Typography>}
          onClick={() => setOpenEmojiPicker(true)}
          clickable
        />
        <Popover
          open={openEmojiPicker}
          anchorEl={emojiPickerAnchorRef.current}
          onClose={() => setOpenEmojiPicker(false)}
          disableRestoreFocus
        >
          <EmojiPicker
            onEmojiSelect={onEmojiSelect}
          />
        </Popover>
      </Grid>
    </Grid>
  );
};
EmojiBar.propTypes = {
  emojis: PropTypes.object.isRequired,
  emojisWithRequesterData: PropTypes.object.isRequired,
  requesterRn: PropTypes.string.isRequired,
  onEmojiSelected: PropTypes.func,
};

export default React.memo(EmojiBar);
